import React, { useState, useMemo } from 'react';
import { Landmark, TrendingDown, TrendingUp, AlertTriangle } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export function EndowmentRunwayCalculator() {
  const [principal, setPrincipal] = useState<number>(1000000);
  const [withdrawalRate, setWithdrawalRate] = useState<number>(4.5);
  const [expectedReturn, setExpectedReturn] = useState<number>(7.0);
  const [inflationRate, setInflationRate] = useState<number>(2.5);

  const model = useMemo(() => {
    const data = [];
    let currentBalance = principal;
    let depletedYear = null;

    for (let year = 1; year <= 50; year++) {
      // Annual calculation
      const returnAmount = currentBalance * (expectedReturn / 100);
      
      // Calculate withdrawal amount adjusted for inflation since year 1
      // Standard endowment practice: Withdraw X% of initial principal, adjusted for inflation each year
      const baseWithdrawal = principal * (withdrawalRate / 100);
      const inflationMultiplier = Math.pow(1 + (inflationRate / 100), year - 1);
      const actualWithdrawal = baseWithdrawal * inflationMultiplier;

      currentBalance = currentBalance + returnAmount - actualWithdrawal;

      if (currentBalance < 0) {
        currentBalance = 0;
        if (!depletedYear) depletedYear = year;
      }

      data.push({
        year,
        balance: Math.round(currentBalance),
        withdrawal: Math.round(actualWithdrawal)
      });
    }

    const finalBalance = data[49].balance;
    const isSustainable = !depletedYear && finalBalance >= principal;

    return {
      data,
      depletedYear,
      finalBalance,
      isSustainable
    };
  }, [principal, withdrawalRate, expectedReturn, inflationRate]);

  const formatCurrency = (val: number) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}k`;
    return `$${val}`;
  };

  const formatFullCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-slate-900 to-indigo-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Landmark className="w-6 h-6 mr-3 text-indigo-400" />
            Endowment Runway Calculator
          </h2>
          <p className="text-indigo-200 max-w-xl">
            Model the long-term sustainability of your alumni scholarship or foundation endowment. Ensure your withdrawal rate doesn't deplete the principal over time when accounting for inflation.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-8">
        
        {/* Controls */}
        <div className="md:col-span-1 space-y-6">
          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                Starting Principal ($)
              </label>
              <Input 
                type="number" 
                value={principal} 
                onChange={(e) => setPrincipal(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                Target Annual Payout (%)
              </label>
              <div className="flex items-center gap-3">
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  step="0.1"
                  value={withdrawalRate} 
                  onChange={(e) => setWithdrawalRate(Number(e.target.value))}
                  className="flex-1 accent-indigo-600"
                />
                <span className="font-bold w-12 text-right">{withdrawalRate}%</span>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                Expected Annual Return (%)
              </label>
              <div className="flex items-center gap-3">
                <input 
                  type="range" 
                  min="0" 
                  max="15" 
                  step="0.5"
                  value={expectedReturn} 
                  onChange={(e) => setExpectedReturn(Number(e.target.value))}
                  className="flex-1 accent-indigo-600"
                />
                <span className="font-bold w-12 text-right">{expectedReturn}%</span>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                Annual Inflation (%)
              </label>
              <div className="flex items-center gap-3">
                <input 
                  type="range" 
                  min="0" 
                  max="10" 
                  step="0.1"
                  value={inflationRate} 
                  onChange={(e) => setInflationRate(Number(e.target.value))}
                  className="flex-1 accent-indigo-600"
                />
                <span className="font-bold w-12 text-right">{inflationRate}%</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Visuals */}
        <div className="md:col-span-3 space-y-6">
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className={`p-6 border-slate-200 dark:border-slate-800 shadow-xl ${model.depletedYear ? 'bg-red-50 dark:bg-red-900/10' : 'bg-emerald-50 dark:bg-emerald-900/10'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Fund Status (Year 50)</h3>
                  {model.depletedYear ? (
                    <div className="text-2xl font-black text-red-600 dark:text-red-400 flex items-center">
                      <TrendingDown className="w-6 h-6 mr-2" /> Depleted in Year {model.depletedYear}
                    </div>
                  ) : (
                    <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 flex items-center">
                      <TrendingUp className="w-6 h-6 mr-2" /> Sustainable
                    </div>
                  )}
                </div>
                {model.depletedYear && <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />}
              </div>
              <p className="text-sm mt-4 text-slate-600 dark:text-slate-400">
                {model.depletedYear 
                  ? `At a ${withdrawalRate}% withdrawal rate, the endowment principal cannot survive inflation and market factors.` 
                  : `The endowment outpaces withdrawals and inflation, leaving a projected balance of ${formatFullCurrency(model.finalBalance)}.`}
              </p>
            </Card>

            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 flex flex-col justify-center">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Year 1 Payout</h3>
              <div className="text-4xl font-black text-slate-900 dark:text-white">
                {formatFullCurrency(principal * (withdrawalRate / 100))}
              </div>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 h-[400px]">
            <h3 className="text-lg font-bold mb-6">50-Year Principal Projection</h3>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={model.data} margin={{ top: 5, right: 20, bottom: 25, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.2} />
                <XAxis 
                  dataKey="year" 
                  tickFormatter={(val) => `Yr ${val}`}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickMargin={10}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tickFormatter={formatCurrency}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip 
                  formatter={(value: any, name: any) => [formatFullCurrency(Number(value)), String(name) === 'balance' ? 'Principal Balance' : 'Adjusted Payout']}
                  labelFormatter={(label) => `Year ${label}`}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <ReferenceLine y={principal} stroke="#94a3b8" strokeDasharray="3 3" label={{ position: 'top', value: 'Starting Principal', fill: '#94a3b8', fontSize: 12 }} />
                <Line 
                  type="monotone" 
                  dataKey="balance" 
                  stroke={model.depletedYear ? '#ef4444' : '#4f46e5'} 
                  strokeWidth={3} 
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

        </div>
      </div>
    </div>
  );
}
